if (!customElements.get('find-your-size')) {
  class FindYourSize extends HTMLElement {
    connectedCallback() {
      const cfgEl = this.querySelector('[data-config]');
      if (!cfgEl) return;
      try { this.cfg = JSON.parse(cfgEl.textContent); }
      catch (e) { console.error('FindYourSize: bad config', e); return; }

      this.unit = 'cm'; // chart data is stored in cm; 'in' converts at the edges
      this.form = this.querySelector('form');
      this.resultEl = this.querySelector('[data-result]');
      this.resultText = this.querySelector('[data-result-text]');
      this.breakdownEl = this.querySelector('[data-breakdown]');
      this.chartEl = this.querySelector('[data-chart]');

      this.buildInputs();
      this.applyVisibility();
      this.buildChart();

      this.form.addEventListener('submit', (e) => { e.preventDefault(); this.recommend(); });
      this.querySelectorAll('[data-unit]').forEach((btn) => {
        btn.addEventListener('click', () => this.setUnit(btn.dataset.unit));
      });
    }

    /* Measurements used for INPUTS and the calculation:
       girth applies to one-pieces only. The chart shows all columns. */
    inputMeasures() {
      return this.cfg.measurements.filter(
        (m) => m !== 'girth' || this.cfg.garment !== 'twopiece'
      );
    }

    cap(s) { return s.charAt(0).toUpperCase() + s.slice(1); }

    buildInputs() {
      const wrap = this.querySelector('[data-inputs]');
      wrap.innerHTML = this.cfg.measurements.map((m) => `
        <div class="fys__field" data-measure="${m}">
          <label for="fys-${m}">${this.cap(m)}</label>
          <input id="fys-${m}" name="${m}" type="number" inputmode="decimal"
                 min="0" step="0.5" placeholder="cm">
        </div>
      `).join('');
    }

    applyVisibility() {
      const active = this.inputMeasures();
      this.querySelectorAll('[data-measure]').forEach((field) => {
        field.hidden = !active.includes(field.dataset.measure);
      });
    }

    /* ---------- size chart (always all measurements) ---------- */

    buildChart() {
      if (!this.chartEl) return;
      if (this.cfg.showChart === false) { this.chartEl.hidden = true; return; }
      this.chartEl.innerHTML = `
        <p class="fys__chart-title">Size chart — fits up to</p>
        <div class="fys__chart-scroll">
          <table class="fys__chart-table">
            <thead>
              <tr>
                <th scope="col">Size</th>
                ${this.cfg.measurements.map((m) => `<th scope="col">${this.cap(m)}</th>`).join('')}
              </tr>
            </thead>
            <tbody data-chart-body></tbody>
          </table>
        </div>
      `;
      this.renderChartBody();
    }

    renderChartBody() {
      if (!this.chartEl || this.cfg.showChart === false) return;
      const body = this.chartEl.querySelector('[data-chart-body]');
      body.innerHTML = this.cfg.sizes.map((size) => `
        <tr>
          <th scope="row">${size.label}</th>
          ${this.cfg.measurements.map((m) => `<td>${this.formatUpper(size[m])}</td>`).join('')}
        </tr>
      `).join('');
    }

    formatUpper([, maxCm]) {
      if (this.unit === 'in') {
        return `${(maxCm / 2.54).toFixed(1).replace(/\.0$/, '')}"`;
      }
      return `${maxCm}cm`;
    }

    /* ---------- units ---------- */

    setUnit(unit) {
      if (unit === this.unit) return;
      const factor = unit === 'in' ? 1 / 2.54 : 2.54;
      this.querySelectorAll('[data-inputs] input').forEach((input) => {
        if (input.value !== '') {
          const v = parseFloat(input.value);
          if (Number.isFinite(v)) input.value = (v * factor).toFixed(1);
        }
        input.placeholder = unit;
        input.step = unit === 'in' ? '0.25' : '0.5';
      });
      this.querySelectorAll('[data-unit]').forEach((btn) => {
        const active = btn.dataset.unit === unit;
        btn.classList.toggle('active', active);
        btn.setAttribute('aria-pressed', String(active));
      });
      this.unit = unit;
      this.renderChartBody();
    }

    toCm(value) { return this.unit === 'in' ? value * 2.54 : value; }

    unitLabel() { return this.unit === 'in' ? '"' : 'cm'; }

    /* ---------- matching ----------

       Per measurement, returns a size index (with the upperBumpCm buffer),
       or special markers:
         { above: true }  — beyond the largest size
         { below: true }  — below the smallest size (see runs_small handling)

       For fit === 'runs_small' (garment runs one size short):
         - the final result is the standard match + 1
         - a standard match on the LAST size therefore counts as above range
         - measurements below the chart, but within one CT→CS step below CT's
           minimum, match a virtual size below CT (index -1) so that +1 lands
           on CT. The step is the offset between CS and CT range starts.
    */

    indexFor(measure, valueCm) {
      const { sizes, upperBumpCm } = this.cfg;
      if (valueCm < sizes[0][measure][0]) return { below: true };
      for (let i = 0; i < sizes.length; i++) {
        const max = sizes[i][measure][1];
        if (valueCm <= max) {
          const bumped = valueCm > max - upperBumpCm && i < sizes.length - 1;
          return { index: bumped ? i + 1 : i, bumped };
        }
      }
      return { above: true };
    }

    recommend() {
      const t = this.cfg.text;
      const runsSmall = this.cfg.fit === 'runs_small';
      const lastIndex = this.cfg.sizes.length - 1;

      const entries = [];
      for (const m of this.inputMeasures()) {
        const raw = this.form[m].value.trim();
        if (raw === '') continue;
        const value = parseFloat(raw);
        if (!Number.isFinite(value) || value <= 0) continue;
        entries.push({ measure: m, display: value, cm: this.toCm(value) });
      }

      if (!entries.length) return this.show(t.invalid, []);

      let best = -Infinity;
      let outOfRange = false;
      const breakdown = [];

      for (const { measure, display, cm } of entries) {
        const prefix = `${this.cap(measure)}: ${display}${this.unitLabel()}`;
        const hit = this.indexFor(measure, cm);

        if (hit.above) {
          outOfRange = true;
          breakdown.push(`${prefix} → above chart`);
          continue;
        }

        let base;
        if (hit.below) {
          if (runsSmall) {
            // virtual size below CT: within one CS−CT step under CT's minimum
            const ctMin = this.cfg.sizes[0][measure][0];
            const step = this.cfg.sizes[1][measure][0] - ctMin;
            if (cm >= ctMin - step) {
              base = -1; // +1 below lands on CT
            } else {
              outOfRange = true;
              breakdown.push(`${prefix} → below chart`);
              continue;
            }
          } else {
            outOfRange = true;
            breakdown.push(`${prefix} → below chart`);
            continue;
          }
        } else {
          base = hit.index;
        }

        if (runsSmall) {
          if (base >= lastIndex) {
            // standard match on the largest size: +1 would exceed the chart
            outOfRange = true;
            breakdown.push(`${prefix} → above chart`);
            continue;
          }
          base += 1;
        }

        best = Math.max(best, base);
        breakdown.push(`${prefix} → ${this.cfg.sizes[base].label}${hit.bumped ? ' (sized up)' : ''}`);
      }

      if (outOfRange) {
        return this.show(t.outOfRange.replace('%EMAIL%', this.cfg.contactEmail || 'us'), breakdown);
      }

      best = Math.max(0, Math.min(best, lastIndex));
      const label = `<strong>${this.cfg.sizes[best].label}</strong>`;
      this.show(t.match.replace('%SIZE%', label), breakdown);
    }

    show(html, breakdown) {
      this.resultText.innerHTML = html;
      this.breakdownEl.innerHTML = breakdown.length > 1 ? breakdown.join('<br>') : '';
      this.resultEl.hidden = false;
    }
  }
  customElements.define('find-your-size', FindYourSize);
}
